/**
 * Akan metnin deposu - React'in DISINDA.
 *
 * ## Neden Query cache'i veya Zustand DEGIL
 *
 * 3000 token'lik bir yanit 40 tok/s'de saniyede ~75 store yazmasi uretiyor.
 * Buyuyen string'i:
 *   - TanStack Query cache'ine koymak (`setQueryData`) -> tum mesaj dizisi
 *     uzerinde structural sharing, saniyede 75 kez,
 *   - Zustand'a koymak -> her `useStore` abonesi saniyede 75 kez yeniden
 *     degerlendiriliyor,
 *   - Context'e koymak -> DAHA KOTU: saniyede 75 kez degisen bir context
 *     value TUM tuketicileri re-render ediyor.
 * Ucu de klasik tuzak.
 *
 * ## Cozum
 *
 * Modul seviyesinde duz bir `Map`. Ag okuyucusu bunu SERBESTCE mutate
 * ediyor, React hic dahil DEGIL. `useSyncExternalStore` ile TAM OLARAK bir
 * bilesen (`<StreamingMessage>`) abone oluyor ve o da frame basina EN FAZLA
 * bir kez re-render ediyor. Query cache'e TAM OLARAK BIR KEZ - `done`da -
 * dokunuluyor.
 */

import { nextShown, needsAnimation, type TyperState } from './typewriter'

export interface StreamBuffer extends TyperState {
  runId: string
  /** Son gorulen SSE `seq` - yeniden baglanmada `?after=` buradan. */
  lastSeq: number
  /** Kullaniciya gosterilecek hata (kaba kod, ham metin DEGIL). */
  error: string | null
  /** Hangi motor kosuyor. */
  model: string | null
  /** Kuyruk pozisyonu (0 = calisiyor, null = bilinmiyor). */
  queuePosition: number | null
  /**
   * Yazilmakta/calismakta olan arac (kalici DEGIL - bkz. sunucuda
   * `runs/live.py`). Gercek arac karti gelince temizleniyor.
   */
  live: LiveTool | null
}

/** `tool_draft` + `tool_progress` olaylarinin birlesik hali. */
export interface LiveTool {
  tool: string
  /** Yazilan parametre (`code`, `prompt`, `find`...). */
  param?: string | null
  /** Parametrenin o ana kadar yazilan metni. */
  text?: string
  /** Arac cagrisi yazildi, arac calisiyor. */
  complete?: boolean
  /** Gorsel: loading | generating | checking | retrying */
  phase?: string
  step?: number
  total?: number
  /** Ara adimin kucuk onizlemesi (data URL). */
  preview?: string
  /** Canli kartin ilk goruldugu an (gecen sure sayaci icin). */
  startedAt: number
}

type Listener = () => void

const buffers = new Map<string, StreamBuffer>()
const listeners = new Map<string, Set<Listener>>()
// Her bildirimde artan sayac. `getSnapshot`in dondurdugu deger bu (asagida
// gerekce).
const versions = new Map<string, number>()

function emptyBuffer(runId: string): StreamBuffer {
  return {
    runId,
    full: '',
    shown: 0,
    floor: 0,
    netDone: false,
    lastSeq: 0,
    error: null,
    model: null,
    queuePosition: null,
    live: null,
  }
}

export function getBuffer(runId: string): StreamBuffer {
  let buffer = buffers.get(runId)
  if (!buffer) {
    buffer = emptyBuffer(runId)
    buffers.set(runId, buffer)
  }
  return buffer
}

export function subscribe(runId: string, listener: Listener): () => void {
  let set = listeners.get(runId)
  if (!set) {
    set = new Set()
    listeners.set(runId, set)
  }
  set.add(listener)
  return () => {
    set!.delete(listener)
    if (set!.size === 0) listeners.delete(runId)
  }
}

function notify(runId: string): void {
  versions.set(runId, (versions.get(runId) ?? 0) + 1)
  const set = listeners.get(runId)
  if (!set) return
  for (const listener of set) listener()
}

/**
 * `useSyncExternalStore` icin anlik goruntu: SURUM SAYACI.
 *
 * Neden buffer'in kendisi DEGIL: `useSyncExternalStore` her bildirimden sonra
 * `getSnapshot()`i yeniden cagirip onceki degerle `Object.is` karsilastirmasi
 * yapiyor. Buffer mutable ve yerinde degistiriliyor, yani her zaman AYNI
 * referans doner -> React "hicbir sey degismedi" diye re-render'i ATLAR ve
 * akan metin ekranda hic guncellenmez.
 *
 * Neden buffer'in bir kopyasi da DEGIL: her cagrida yeni nesne dondurmek
 * React'te "getSnapshot should be cached" sonsuz dongu uyarisini tetikliyor.
 *
 * Artan bir sayi ikisini de cozuyor: kararli (iki cagri arasinda degismiyor)
 * ve degisimi bildiriyor. Cagiran sonra `getBuffer`/`visibleText` ile guncel
 * degerleri okuyor.
 */
export function getSnapshot(runId: string): number {
  return versions.get(runId) ?? 0
}

// ------------------------------------------------------------------ yazma

/** Canli token metni ekler. */
export function appendText(runId: string, text: string, seq: number): void {
  const buffer = getBuffer(runId)
  buffer.full += text
  buffer.lastSeq = Math.max(buffer.lastSeq, seq)
  notify(runId)
}

/**
 * Replay edilmis metni ANINDA commit eder.
 *
 * `floor` yukseltiliyor: 3000 karakteri yeniden daktilo etmek saniyeler
 * surer ve kullaniciya "uygulama gecmisi yeniden yaziyor" gibi gorunur.
 */
export function commitResume(runId: string, text: string, seq: number): void {
  const buffer = getBuffer(runId)
  buffer.full += text
  buffer.floor = buffer.full.length
  buffer.shown = buffer.full.length
  buffer.lastSeq = Math.max(buffer.lastSeq, seq)
  notify(runId)
}

/** Gecmisten yuklenen tamamlanmis metin - typer'a HIC girmiyor. */
export function seedFromHistory(runId: string, text: string): void {
  const buffer = getBuffer(runId)
  buffer.full = text
  buffer.floor = text.length
  buffer.shown = text.length
  buffer.netDone = true
  notify(runId)
}

export function setMeta(runId: string, meta: { model?: string | null; seq?: number }): void {
  const buffer = getBuffer(runId)
  if (meta.model !== undefined) buffer.model = meta.model
  if (meta.seq !== undefined) buffer.lastSeq = Math.max(buffer.lastSeq, meta.seq)
  notify(runId)
}

export function setQueuePosition(runId: string, position: number | null): void {
  const buffer = getBuffer(runId)
  buffer.queuePosition = position
  notify(runId)
}

export function setError(runId: string, message: string): void {
  const buffer = getBuffer(runId)
  buffer.error = message
  notify(runId)
}

/** Ag akisi bitti: kalan metin animasyonsuz commit ediliyor. */
export function finish(runId: string): void {
  const buffer = getBuffer(runId)
  buffer.netDone = true
  buffer.queuePosition = null
  buffer.live = null
  notify(runId)
}

/**
 * Canli arac bilgisini gunceller. `null` temizler (gercek kart geldi).
 *
 * Farkli bir arac icin gelen taslak oncekinin YERINI aliyor; ayni aracin
 * olaylari (taslak -> calisiyor -> adimlar -> onizleme) birlesiyor. Onizleme
 * yalnizca yenisi gelince degisiyor - aradaki adim olaylarinda eski onizleme
 * kaybolup yanip sonmesin.
 */
export function setLive(runId: string, patch: Partial<LiveTool> | null): void {
  const buffer = getBuffer(runId)
  if (patch === null) {
    if (buffer.live === null) return
    buffer.live = null
  } else {
    const current = buffer.live
    const sameTool = current !== null && (!patch.tool || patch.tool === current.tool ||
      (current.tool.endsWith('_image') && patch.tool === 'image'))
    const base: LiveTool = sameTool && current ? current : { tool: patch.tool ?? 'tool', startedAt: Date.now() }
    const merged: LiveTool = { ...base, ...patch, tool: base.tool, startedAt: base.startedAt }
    if (patch.preview === undefined && base.preview) merged.preview = base.preview
    buffer.live = merged
  }
  notify(runId)
}

/**
 * Tarayici gorunurlugu/hareket tercihi degisti: kalani ANINDA commit et.
 *
 * Gizli sekmede rAF ~1 Hz'e kisiliyor; bu olmadan typer binlerce karakter
 * geriye duser ve donuste gorunur bir sarsintiyla yetisir.
 */
export function commitAll(runId: string): void {
  const buffer = getBuffer(runId)
  buffer.floor = buffer.full.length
  buffer.shown = buffer.full.length
  notify(runId)
}

// -------------------------------------------------------------- animasyon

/**
 * Bir frame ilerlet. Donus: gorunur metin degisti mi.
 *
 * rAF dongusu bunu cagiriyor; React yalnizca `true` donunce haberdar
 * ediliyor, yani frame basina EN FAZLA bir re-render.
 */
export function tick(runId: string): boolean {
  const buffer = buffers.get(runId)
  if (!buffer) return false
  const target = nextShown(buffer)
  if (target === buffer.shown) return false
  buffer.shown = target
  notify(runId)
  return true
}

export function isAnimating(runId: string): boolean {
  const buffer = buffers.get(runId)
  return buffer ? needsAnimation(buffer) : false
}

/** Gorunur metin (typer'in o anki hali). */
export function visibleText(runId: string): string {
  const buffer = buffers.get(runId)
  if (!buffer) return ''
  return buffer.full.slice(0, buffer.shown)
}

export function drop(runId: string): void {
  buffers.delete(runId)
  listeners.delete(runId)
  versions.delete(runId)
}

/** Testler icin. */
export function resetAll(): void {
  buffers.clear()
  listeners.clear()
  versions.clear()
}
